import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { generateAiDraft } from '#src/app/pages/startAPetitionV2/shared/api';
import { useImageJob } from '#src/app/pages/startAPetitionV2/shared/hooks';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useTrackOnce } from '#src/app/shared/hooks/tracking';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

type SubmitInput = { issue: string; personalStory: string };

type UseChatAppEntryReturn = ModelHookResult<
	{
		problem?: string | null;
		loading: boolean;
		error: Error | null;
	},
	{
		submitFromMainCta: () => void;
		submitFromFooterCta: () => void;
		trackCtaSectionView: () => void;
	}
>;

/**
 * Hook for the Chat App entry landing step. On submit, uses the draft input
 * to trigger AI draft generation and transition to preview.
 */
export function useChatAppEntry(): UseChatAppEntryReturn {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();
	const trackOnce = useTrackOnce();

	const utilityContext = useUtilityContext();
	const { draft } = data;

	const { mutateAsync: createJobAsync } = useImageJob();

	//  Problem param is passed from ChatGPT app to be used as a petition title for the
	// card on the landing page. Adding a fallback in case it is missing.
	const problemParam = useQueryParamValue('problem');
	// TODO issue might not be the best fallback as it is bullet points.
	const problem = problemParam ? decodeURIComponent(problemParam) : draft?.input?.issuePrompt;

	const { mutate, error, ...mutationState } = useMutation({
		mutationFn: async (input: SubmitInput) => {
			const trimmed = {
				mainPrompt: input.issue.trim(),
				personalStory: input.personalStory.trim(),
			};

			const [aiDraftResponse, imageResponse] = await Promise.allSettled([
				generateAiDraft(trimmed, utilityContext, track),
				createJobAsync(trimmed),
			]);

			if (aiDraftResponse.status === 'rejected') throw aiDraftResponse.reason;

			const image =
				imageResponse.status === 'fulfilled' ? { image: { responseId: imageResponse.value.jobId } } : undefined;

			return { ...aiDraftResponse.value, ...image, input: trimmed };
		},
		onMutate: (input) => {
			void track('sap_step_submit', {
				step: 'chat_app_entry',
				step_data: { prompt: input.issue, personal_story: input.personalStory },
				step_data_raw: JSON.stringify({ prompt: input.issue, personal_story: input.personalStory }),
			});
		},
		onSuccess: (response) => {
			const recommendedDraft = { ask: response.ask, description: response.description, image: null };
			const metadata = { imageJobId: response.image?.responseId ?? null };
			actions.send({
				type: 'submit',
				draft: {
					input: { issuePrompt: response.input.mainPrompt, personalStory: response.input.personalStory },
					recommendedDraft,
					petitionDraft: recommendedDraft,
					metadata,
				},
			});
		},
		onError: (e) => {
			void track('sap_chat_app_entry_submit_error', { error: e.constructor.name });
		},
	});

	const getDraftInput = useCallback(
		(): SubmitInput => ({
			issue: draft?.input?.issuePrompt ?? '',
			personalStory: draft?.input?.personalStory ?? '',
		}),
		[draft],
	);

	const submitFromMainCta = useCallback(() => {
		void track('sap_chat_app_entry_main_cta_click');
		mutate(getDraftInput());
	}, [mutate, track, getDraftInput]);

	const submitFromFooterCta = useCallback(() => {
		void track('sap_chat_app_entry_footer_cta_click');
		mutate(getDraftInput());
	}, [mutate, track, getDraftInput]);

	const trackCtaSectionView = useCallback(() => {
		void trackOnce('sap_chat_app_entry_cta_footer_section_view');
	}, [trackOnce]);

	return {
		data: {
			problem,
			loading: isLoading(mutationState) || isLoaded(mutationState),
			error,
		},
		actions: { submitFromMainCta, submitFromFooterCta, trackCtaSectionView },
	};
}
